// Phase 6.3.3 (S3.2) — resolve Slack group-DM (mpim) names.
//
// Slack names group DMs like "mpdm-chinmoyb--piyushm--sudarshank-1"
// (handles joined by "--", "mpdm-" prefix, "-1" suffix). Raw, that's
// unreadable; resolve the handles to display names via the workspace
// user cache → "Chinmoy, Piyush, Sudarshan + 1 more".

import type { ApiUser } from "./api";

function displayNameFor(u: ApiUser): string {
  return (
    u.display_name ||
    u.real_name ||
    u.name ||
    "?"
  );
}

/** Map each user's Slack handle (the `name` field) → display name. */
export function buildHandleMap(
  users: Record<string, ApiUser>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const u of Object.values(users)) {
    if (typeof u.name === "string" && u.name) {
      m.set(u.name, displayNameFor(u));
    }
  }
  return m;
}

/** Parse the handles out of an mpdm- name. Returns [] if not an
 *  mpdm name or unparseable. */
function parseMpdmHandles(rawLabel: string): string[] {
  if (!rawLabel.startsWith("mpdm-")) return [];
  const body = rawLabel.slice("mpdm-".length).replace(/-\d+$/, "");
  return body.split("--").map((h) => h.trim()).filter(Boolean);
}

/**
 * Format an mpdm- conversation label into readable display names.
 * Non-mpdm labels pass through unchanged. Unresolvable handles fall
 * back to "Group DM (N people)"; an unparseable name returns as-is.
 */
export function formatGroupDmLabel(
  rawLabel: string,
  handleMap: Map<string, string>,
): string {
  const handles = parseMpdmHandles(rawLabel);
  if (handles.length === 0) return rawLabel; // not an mpdm / unparseable

  const names = handles
    .map((h) => handleMap.get(h))
    .filter((n): n is string => Boolean(n));

  if (names.length === 0) {
    return `Group DM (${handles.length} people)`;
  }

  const head = names.slice(0, 3).join(", ");
  const moreCount = handles.length - Math.min(3, names.length);
  return moreCount > 0 ? `${head} + ${moreCount} more` : head;
}
